// @flow
/** @jsx jsx */

import { createKeyHandler } from '@zapier/common-utils';
import { Border, Forms } from '@zapier/theme';
import { css, jsx } from '@emotion/core';
import { Colors } from '@zapier/design-system';

import { matchTokensAndSpace } from 'app/common/CommonUtils';

import { type Node } from 'react';

const DECORATOR_COLORS = {
  default: Colors.UiPrimary,
};

const getDecoratorColor = (decoratorText = '') => {
  const [type] = decoratorText.split('.');

  return DECORATOR_COLORS[type] || DECORATOR_COLORS.default;
};

const textBoxStyles = showError =>
  css({
    border: Forms.field.border,
    borderRadius: Border.radius.default,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0px 10px',
    overflow: 'auto',
    whiteSpace: 'nowrap',
    ...(showError ? { borderColor: Colors.UiBrand } : {}),
  });

const placeholderCss = css({
  color: Colors.TextDefault,
});

const urlInputTextCss = css({
  // Prevents certain instances of this component from overflowing its parent
  // (see https://github.com/zapier/zapier/pull/25523#issuecomment-481948501).
  width: '0',
});

const getDecoratorStyles = ({ text }) =>
  css({
    fontWeight: 600,
    color: getDecoratorColor(text),
  });

type Props = {
  ariaLabel: string,
  Decorator: (props: any) => Node,
  hasError: boolean,
  onFocus: () => void,
  placeholder: string,
  value: string,
};

const applyDecorations = props => {
  const { value, Decorator } = props;
  const parts = value.split(matchTokensAndSpace);

  const decoratedParts = [];
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (part === '{{' && parts[i + 2] === '}}') {
      decoratedParts.push(<Decorator key={i} value={parts[i + 1]} />);
      // We've accounted for {{<tokenValue>}} so we can be up to the index after the closing token
      i += 2;
    } else if (part === ' ') {
      decoratedParts.push(<span key={i}>&nbsp;</span>);
    } else {
      decoratedParts.push(part);
    }
  }

  return decoratedParts;
};

const ReadOnly = (props: Props) => (
  <div
    aria-label={props.ariaLabel}
    css={textBoxStyles(props.hasError)}
    onClick={props.onFocus}
    onFocus={props.onFocus}
    onKeyDown={createKeyHandler(['enter'], props.onFocus)}
    role="textbox"
    tabIndex="0"
  >
    {props.value ? (
      <span css={urlInputTextCss}>{applyDecorations(props)}</span>
    ) : (
      <span css={placeholderCss}>{props.placeholder}</span>
    )}
  </div>
);

ReadOnly.defaultProps = {
  Decorator: ({ value }) => (
    <span css={getDecoratorStyles(value)}>{`{{${value}}}`}</span>
  ),
};

export default ReadOnly;
